import { Alert, Box, Button as MuiButton, Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import ReviewScreen from '@/karehero-app/tell-us-once/intake/ReviewScreen';
import PlaceholderScreen from '@/karehero-app/tell-us-once/intake/PlaceholderScreen';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';
import { CARER_INITIALS } from '@/karehero-app/tell-us-once/intake/content';
import CarriedScreen from '@/karehero-app/tell-us-once/benefits/CarriedScreen';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import CareHubScreen from '@/karehero-app/care/CareHubScreen';
import AaClaim, { AaReview } from '@/karehero-app/care/AaClaim';
import {
  AA_CHAT_BACK,
  AA_CHAT_DISTRESS,
  AA_CHAT_VAGUE,
} from '@/karehero-app/care/AaClaim.content';
import {
  AA_BAR,
  AA_CARRIED,
  AA_CHAT,
  AA_CHAT_SECTIONS,
  AA_PAPERWORK_FILES,
  AA_START,
} from '@/karehero-app/tell-us-once/AttendanceAllowance.content';

const noop = () => {};

// Money in Joining up, with the claim this many stages in, and its drawer up
// if asked.
const aaHub =
  (aaDone: number, open = false) =>
  () => (
    <CareHubScreen
      grid
      tidy
      subjects
      initialArea='money'
      aaDone={aaDone}
      openClaim={open ? 'Attendance Allowance' : undefined}
    />
  );

// The claim's bar, on every screen inside it, so no screen of the claim says
// Benefits calculator at the top.
const bar = <CalculatorBar content={AA_BAR} />;

/** A step the claim has to have and nothing has drawn. */
const missing = (title: string, note: string) => () => (
  <PlaceholderScreen title={title} note={note} />
);

/**
 * Where the care questions start, past the paperwork offer and the reading.
 *
 * Found rather than counted: the turns in front of these have changed twice
 * already, and a number would go wrong silently each time.
 */
const firstCareQuestion = AA_CHAT.findIndex(
  (q) => q.id !== 'paperwork' && q.id !== 'processing',
);

// The care questions, asked in the intake's chat. `fresh` so each one is the
// screen rather than the bottom of a long transcript.
//
// `replied` where a step should show the question answered rather than only
// asked: the first of these takes a typed answer, and a composer with nothing
// in it shows the field without showing what it is for.
const asked = (step: number, replied?: string) => () => (
  <ChatV2
    questions={AA_CHAT}
    startAt={step}
    fresh
    onExit={noop}
    bar={bar}
    replied={replied}
  />
);

// The paperwork turn with a couple of things handed over. The way on is still
// there underneath: handing something over is not the end of the turn.
const added = () => (
  <ChatV2
    questions={AA_CHAT}
    startAt={0}
    fresh
    onExit={noop}
    bar={bar}
    uploaded='document'
    uploadedName={AA_PAPERWORK_FILES}
  />
);

/**
 * The reading, as its own conversation.
 *
 * Continue closes the paperwork turn, so this opens on a cleared transcript —
 * AA_PROCESSING is a `restart` — and says what it is doing. The dots under it
 * are the rest of the state, held open because they are the whole of it.
 *
 * It was ProcessingScreen: a full page that takes the conversation away in
 * order to say something about it.
 */
const reading = () => (
  <ChatV2
    questions={AA_CHAT}
    startAt={AA_CHAT.findIndex((q) => q.id === 'processing')}
    fresh
    onExit={noop}
    bar={bar}
    // Held open. A handover that let the dots time out would show a message
    // with nothing under it, which is the one thing this step is about.
    reading
  />
);

/**
 * The Attendance Allowance claim, in the patterns Tell Us Once established.
 *
 * The questions are the real form's, read from KareHero's normalised AA schema.
 * Two hundred and twenty-seven of them across seventeen sections, which is the
 * fact the design has to answer: the walked section shows the four sections the
 * claim turns on being asked in our patterns.
 */
export const attendanceAllowanceFlowsSpec: HandoverSpec = {
  id: 'attendance-allowance',
  title: 'Attendance allowance',
  description:
    'The real AA questions, mapped onto the patterns the intake and the calculator established. The care sections walked as the chat would ask them.',
  links: [
    { label: 'The patterns', url: '/tell-us-once/flows/patterns' },
    {
      label: 'Benefits calculator',
      url: '/tell-us-once/flows/benefits-calculator',
    },
    {
      label: 'Onboarding 1.0',
      url: '/tell-us-once/flows/intake-profile-review',
    },
    { label: 'The form as a form', url: '/tell-us-once/exploration' },
  ],
  flows: [
    // The claim as it's being designed now, in the calculator's chat, from
    // Money in Joining up.
    {
      title: 'Attendance Allowance',
      steps: [
        {
          id: 'aa-money',
          title: 'On Money, not started',
          render: aaHub(0, true),
        },
        {
          id: 'aa-start',
          title: 'Starting with special rules',
          render: () => <AaClaim />,
          alt: {
            title: 'Or a percentage under the title, no segments',
            render: () => <AaClaim percent />,
          },
        },
        {
          id: 'aa-about',
          title: 'Then what we already have',
          render: () => <AaClaim at='about' />,
        },
        {
          id: 'aa-upload',
          title: 'A photo, where it is quicker',
          render: () => <AaClaim at='medicines' />,
        },
        {
          id: 'aa-consent',
          title: 'Consent, said like any answer',
          render: () => <AaClaim at='consent' />,
        },
        {
          id: 'aa-consent-vague',
          title: 'Only asked again if vague',
          render: () => (
            <AaClaim questions={AA_CHAT_VAGUE} at='consent-again' />
          ),
        },
        {
          id: 'aa-distress',
          title: 'Upset, so it stops',
          render: () => <AaClaim questions={AA_CHAT_DISTRESS} at='stop' />,
        },
        {
          id: 'aa-readback',
          title: "Health's read-back",
          render: () => <AaClaim at='health-readback' />,
        },
        {
          id: 'aa-answers',
          title: 'Its answers so far, by stage',
          render: () => (
            <CareHubScreen
              grid
              tidy
              subjects
              initialArea='money'
              aaDone={2}
              initialAaAnswers
            />
          ),
          alt: [
            {
              title: 'Or straight back into the chat',
              render: () => <AaClaim questions={AA_CHAT_BACK} at='back' />,
            },
          ],
        },
        {
          id: 'aa-finished',
          title: 'Every stage answered, then a call',
          render: () => (
            <CareHubScreen
              grid
              tidy
              subjects
              initialArea='money'
              aaDone={5}
              aaFinished
              initialAaAnswers
            />
          ),
          alt: [
            {
              title: "If it looks like Margaret can't get it",
              render: () => (
                <CareHubScreen
                  grid
                  tidy
                  subjects
                  initialArea='money'
                  aaDone={5}
                  aaFinished
                  aaNotEligible
                  initialAaAnswers
                />
              ),
            },
          ],
        },
        {
          id: 'aa-back',
          title: 'Coming back to it',
          render: aaHub(2),
        },
      ],
    },
    // The finished claim, checked a section at a time.
    {
      title: 'Reviewing the answers, a section at a time',
      steps: [
        {
          id: 'aa-review-check',
          title: 'First, what needs them',
          render: () => <AaReview />,
        },
        {
          id: 'aa-review-health',
          title: "Margaret's health",
          render: () => <AaReview initialSection={2} />,
        },
        {
          id: 'aa-review-care',
          title: "Margaret's care, day and night",
          render: () => <AaReview initialSection={4} />,
        },
      ],
    },
    // Earlier work, kept shut: the claim drawn before Joining up.
    {
      title: 'The claim, end to end',
      collapsed: true,
      divider: true,
      steps: [
        {
          id: 'carried',
          title: 'What the claim already has',
          tap: 'Into the claim',
          render: () => (
            <CarriedScreen
              onStart={noop}
              bar={bar}
              content={AA_START}
              carried={AA_CARRIED}
            />
          ),
        },
        {
          id: 'evidence',
          title: 'The evidence, offered in the chat',
          tap: 'Confirmed',
          render: asked(0),
        },
        {
          id: 'added',
          title: 'A couple of things handed over',
          tap: '‘Add paperwork’',
          render: added,
        },
        {
          id: 'reading',
          title: 'Reading what they sent',
          tap: 'Sent something to read',
          render: reading,
          alt: {
            // The other way out of the paperwork turn. Continue is under the
            // offer whether or not anything was handed over, so this is the
            // same tap with nothing behind it.
            title: 'Or straight in, with nothing to read',
            render: asked(firstCareQuestion),
          },
          note: (
            <Note title='Readme' variant='body2'>
              A new conversation, not a screen. Continue closes the paperwork
              turn, so the claim clears down and says what it is doing, and the
              dots stay under it until the reading is done. A full page would
              take the conversation away in order to say something about it.
              Continue with nothing handed over goes straight to the questions,
              which is the row underneath: there is nothing to read, so there is
              nothing to say about reading it.
            </Note>
          ),
        },
        {
          id: 'questions',
          title: 'The care questions',
          tap: 'Once it has been read',
          // The first one is a date, typed rather than picked, so this is the
          // step that shows the composer doing something.
          render: asked(
            firstCareQuestion,
            'Since her fall, around June last year',
          ),
          note: (
            <Note title='Readme' variant='body2'>
              After the reading, not alongside it. What comes out of the
              paperwork answers some of these, so asking first would be asking
              for things we are about to be told, and the carer would answer
              them twice.
            </Note>
          ),
        },
        {
          // The 220-odd questions between the first one and the review. Drawn
          // as one marker rather than a row of frames: the patterns each of
          // them takes are the tables above, and a step per question would be
          // a flows page nobody can read.
          id: 'rest',
          title: 'Rest of AA flow',
          tap: 'Question after question',
          render: missing(
            'Rest of AA flow',
            'The remaining care questions, asked the same way. See the tables above for which pattern each one takes.',
          ),
        },
        {
          id: 'review',
          title: 'The review',
          tap: 'Nothing left to ask',
          render: () => <ReviewScreen bar={bar} />,
        },
        {
          id: 'sent',
          title: 'Form complete',
          tap: '‘Finish’',
          render: () => (
            <Box
              sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}
            >
              <CareProfileScreen
                initialTab='claims'
                account={{ initials: CARER_INITIALS }}
              />
              {/* The Alert on its own, placed here rather than inside a
                  Snackbar. Snackbar is only a positioner — fixed, pinned to
                  left:50% with a pull-back transform, capped at 400 by llama —
                  and every one of those had to be overridden to sit in a phone
                  and span its gutters. Overriding all of a component is a sign
                  you wanted the thing inside it. */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 16,
                  right: 16,
                  bottom: 88,
                  zIndex: 20,
                }}
              >
                <Alert
                  severity='success'
                  action={
                    <MuiButton color='inherit' size='small' onClick={noop}>
                      Book a call
                    </MuiButton>
                  }
                >
                  Form complete. Book a call and we will take you through what
                  happens next.
                </Alert>
              </Box>
            </Box>
          ),
          note: (
            <Note title='Readme' variant='body2'>
              <Stack spacing={1.5}>
                <span>
                  No screen of its own. Finishing the form is the end of the
                  questions, not a place, so it puts them back where the claim
                  started — the Assess tab, or Home if that is where they came
                  in — and says what happened over the top of it.
                </span>
                <span>
                  The toast asks for the call rather than congratulating them. A
                  finished form is not the thing that helps; the call about what
                  happens next is, and this is the one moment we have their
                  attention for it.
                </span>
                <span>
                  What actually leaves us is still open. The DWP takes AA by
                  post, by phone or online, and which of those we do decides
                  whether there is a PDF to print or a form we fill on her
                  behalf behind this.
                </span>
              </Stack>
            </Note>
          ),
        },
        {
          id: 'after',
          title: 'Coming back to it',
          tap: 'Form complete',
          render: missing(
            'Waiting on a decision',
            'Not drawn. Weeks, not minutes, so the way back in has to come and find them: the ping the intake uses for reading that finishes after they have gone.',
          ),
        },
      ],
    },
    // One row per care section, over one conversation. Every question at every
    // depth, in the form's order, so the repetition is visible rather than
    // described: a yes/no gate, the ways it is true as a pick-several, then how
    // often each day and how many minutes each time.
    ...AA_CHAT_SECTIONS.map((section) => ({
      title: `As the chat would ask it: ${section.label}`,
      collapsed: true,
      steps: AA_CHAT.slice(section.from, section.from + section.count).map(
        (q, i) => ({
          id: `ask-${q.id}`,
          title: q.ask,
          tap: i === 0 ? 'Into the questions' : (q.answer ?? 'Answered'),
          render: asked(section.from + i),
        }),
      ),
    })),
    {
      // The work that was already here, kept as it was: it is the one part of
      // the claim that has been designed, and the question it asks is still open.
      title: 'Reading the claim, and changing something in it',
      collapsed: true,
      note: (
        <Note title='Readme' variant='body2'>
          Drawn against the calculator and moved here, because the review
          belongs to the claim. Option A is what exists: the count opens a
          drawer, and a row becomes a text field where it sits. Option B is the
          alternative underneath each step: the count expands in place, and the
          drawer is kept for the row, where it can carry the control the answer
          actually takes.
        </Note>
      ),
      steps: [
        {
          id: 'claim',
          title: 'The review',
          tap: 'Into the claim',
          render: () => <ReviewScreen bar={bar} />,
        },
        {
          id: 'claim-hidden',
          title: 'What is behind the count',
          tap: '‘14 more, all answered’',
          render: () => (
            <ReviewScreen bar={bar} initialExpanded='About Margaret' />
          ),
        },
        {
          id: 'claim-edit',
          title: 'Changing one',
          tap: '‘Her address’',
          // A pick-several, which is the case a text field in the row cannot
          // serve at all: this is why a row opens in a drawer.
          render: () => (
            <ReviewScreen
              bar={bar}
              initialExpanded='About Margaret'
              initialEditing='in-payment'
            />
          ),
        },
      ],
    },
  ],
  states: [],
  components: {
    existing: [
      'ChatV2',
      'ReviewScreen',
      'FactList',
      'CarriedScreen',
      'ChatTyping',
      'CalculatorBar',
    ],
  },
};
